import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verified: boolean;
  profile_picture_url?: string;
  two_factor_enabled?: boolean;
  theme?: 'light' | 'dark';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string, twoFactorCode?: string, backupCode?: string) => Promise<{ requires_2fa: boolean } | void>;
  register: (data: RegisterData, profilePicture?: File | null) => Promise<number>;
  verifyEmail: (userId: number, code: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  password2: string;
  first_name?: string;
  last_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token && !user) {
      // Only fetch if we don't already have a user (prevents clearing OAuth-set user)
      fetchUser();
    } else {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  const fetchUser = async () => {
    try {
      console.log('[AuthContext] Fetching user profile...');
      const response = await authAPI.getProfile();
      const userData = response.data;
      console.log('[AuthContext] User profile fetched:', { 
        id: userData.id, 
        username: userData.username, 
        theme: userData.theme 
      });
      setUser(userData);
      // Apply dashboard theme immediately if we're on a dashboard route
      const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
      if (isDashboardRoute && userData.theme) {
        console.log('[AuthContext] Applying theme from user data:', userData.theme);
        // Apply immediately and multiple times to ensure it sticks
        const applyTheme = () => {
          document.documentElement.setAttribute('data-theme', userData.theme);
        };
        applyTheme(); // Immediate
        setTimeout(applyTheme, 0);
        setTimeout(applyTheme, 10);
        setTimeout(applyTheme, 50);
        setTimeout(applyTheme, 100);
        setTimeout(applyTheme, 200);
        setTimeout(applyTheme, 500);
      }
      if (import.meta.env.DEV) {
        console.log('[AuthContext] fetchUser success', userData.id);
      }
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('AuthContext: fetchUser error', error);
      }
      
      // Check error type
      const isAuthError = error?.response?.status === 401;
      const isNetworkError = !error?.response; // No response means network/server error
      const refreshToken = localStorage.getItem('refresh_token');
      
      // IMPORTANT: Only clear tokens if we're absolutely sure they're invalid
      // The API interceptor handles token refresh automatically, so we should be conservative
      
      if (isNetworkError) {
        // Network/server error (server restart, offline, etc.) - KEEP tokens
        // User might be temporarily offline or server is restarting
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Network/server error - keeping tokens (server might be restarting)');
        }
        // Don't clear tokens - might be temporary
      } else if (isAuthError && refreshToken) {
        // 401 error but we have refresh token - interceptor will handle refresh
        // If refresh fails, interceptor will clear tokens and redirect to login
        // So we don't need to clear here
        if (import.meta.env.DEV) {
          console.log('[AuthContext] 401 error but refresh token exists - interceptor will handle refresh');
        }
        // Keep tokens - let interceptor handle it
      } else if (isAuthError && !refreshToken && !user) {
        // 401 error, no refresh token, and no user - tokens are definitely invalid
        // This is the only case where we clear tokens
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Clearing tokens - 401 error, no refresh token, no user');
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        setUser(null);
      } else {
        // Any other error or we have a user - keep tokens
        // This is conservative approach to prevent unnecessary logouts
        if (import.meta.env.DEV) {
          console.log('[AuthContext] Keeping tokens - conservative approach');
        }
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const login = async (username: string, password: string, twoFactorCode?: string, backupCode?: string) => {
    const response = await authAPI.login(username, password, twoFactorCode, backupCode);
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true };
    }
    
    const { access, refresh, user: userData } = response.data;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    setUser(userData);
    // Apply dashboard theme if we're on a dashboard route
    const isDashboardRoute = window.location.pathname.startsWith('/dashboard');
    if (isDashboardRoute && userData.theme) {
      document.documentElement.setAttribute('data-theme', userData.theme);
    }
    return { requires_2fa: false };
  };

  const register = async (data: RegisterData, profilePicture?: File | null): Promise<number> => {
    // If profile picture is provided, use FormData
    if (profilePicture) {
      const formData = new FormData();
      formData.append('username', data.username);
      formData.append('email', data.email);
      formData.append('password', data.password);
      formData.append('password2', data.password2);
      if (data.first_name) formData.append('first_name', data.first_name);
      if (data.last_name) formData.append('last_name', data.last_name);
      formData.append('profile_picture', profilePicture);
      
      const response = await authAPI.register(formData);
      return response.data.user_id;
    } else {
      const response = await authAPI.register(data);
      return response.data.user_id;
    }
  };

  const verifyEmail = async (userId: number, code: string) => {
    const response = await authAPI.verifyEmail(userId, code);
    const { access, refresh, user: userData } = response.data;
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);
  };

  // Check authentication based on both user state and token presence
  const isAuthenticated = !!user || !!localStorage.getItem('access_token');

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        verifyEmail,
        logout,
        isAuthenticated,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

